/**
 * Shared formatting utilities for currency, dates, and amounts.
 */

export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    // Normalise -0 (arises from float subtractions like (-0)/100) so it never
    // renders the misleading "-$0.00" (LR-208 r6 LOW).
  }).format(value === 0 ? 0 : value);
}

export function formatCompactCurrency(value: number, currency = 'USD'): string {
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  return formatCurrency(value, currency);
}

/**
 * Today's date as a local-zone `YYYY-MM-DD` key (e.g. "2026-05-29").
 *
 * Built from `Date.get*` (local components), NOT `new Date().toISOString()`
 * — the ISO path always slices the UTC day, so in UTC-negative timezones the
 * trader's evening clicks would silently emit "tomorrow UTC" as the wire date
 * for legal-effect forms (notice / terminate / settlement-date). The local-
 * components path always returns the day the trader sees in their UI clock
 * (LR-208 r5 M1). Mirrors `dayKeyOf` in features/trades/utils/activity-grouping.ts.
 */
export function todayLocalKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Today's date as a **UTC** `YYYY-MM-DD` key (e.g. "2026-05-29") — the mirror of
 * `todayLocalKey()` for gates that must agree with the backend, which computes
 * "today" in UTC (`datetime.now(timezone.utc).date()`, TIME_ZONE='UTC').
 *
 * Use this — NOT `todayLocalKey()` — when comparing a serialized backend date
 * against "today" so the FE decision matches the server's (e.g. the LR-544
 * future-trade-date gate). For legal-effect wire dates the trader types (notice
 * / terminate / settlement-date), keep `todayLocalKey()`.
 */
export function todayUtcKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Same as todayLocalKey() but adds an integer number of days. Used by the
 *  notice modal "Effective Date" preview so the displayed value matches the
 *  same local-day arithmetic the wire payload uses. */
export function localDateKeyPlusDays(daysToAdd: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysToAdd);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Resolve a backend date to its UTC calendar components. Backend DateFields
 * serialise as a bare `YYYY-MM-DD` (a calendar date with NO timezone) — those are
 * read from their literal Y-M-D so the day can never shift with the viewer's zone
 * (a bare date parsed via `new Date()` is UTC-midnight, which a UTC-negative desk
 * used to see as the previous day — LR-208 r6 H1). Datetimes (`T..Z`) resolve to
 * their UTC day, the desk's one-timezone rule (same as `formatDateTimeUtc`).
 * Returns null for null / unparseable input.
 */
function utcDateParts(dateStr: string | null): { y: string; m: string; d: string } | null {
  if (!dateStr) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (dateOnly) return { y: dateOnly[1], m: dateOnly[2], d: dateOnly[3] };
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return {
    y: String(d.getUTCFullYear()),
    m: String(d.getUTCMonth() + 1).padStart(2, '0'),
    d: String(d.getUTCDate()).padStart(2, '0'),
  };
}

/**
 * Format a backend date for display as ISO `YYYY-MM-DD` — the desk's date
 * convention (LR-667), matching the `YYYY-MM-DD HH:MM UTC` timestamps, the API/CSV
 * wire format and fixed-width column alignment (the previous "Aug 14, 2026" was
 * variable-width and read as a US-locale outlier next to them). UTC-from-literal
 * components, see {@link utcDateParts}. Unparseable input → em dash.
 */
export function formatDate(dateStr: string | null): string {
  const p = utcDateParts(dateStr);
  return p ? `${p.y}-${p.m}-${p.d}` : '—';
}

/**
 * Compact `MM-DD` (no year) — the dense companion to {@link formatDate} for chart
 * axes where the year is carried once in a header and repeating it on every tick
 * wastes width (LR accrual chart). Same UTC-from-literal path, so a bare
 * `YYYY-MM-DD` renders as exactly the calendar date the backend meant.
 * Unparseable input → em dash.
 */
export function formatDateShort(dateStr: string | null): string {
  const p = utcDateParts(dateStr);
  return p ? `${p.m}-${p.d}` : '—';
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

/**
 * 24-hour UTC time only ("14:02 UTC") — Activity timestamps are pinned to UTC so
 * the desk reads one timezone regardless of the viewer's locale. The day-group
 * header carries the date; the row carries this time.
 */
export function formatTimeUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
}

/**
 * "YYYY-MM-DD HH:MM UTC" — full date + 24-hour UTC time. Used by the inventory
 * Statement picker so snapshot timestamps (stored/scheduled in UTC, e.g. the
 * 22:00-UTC evening snapshot) read consistently for every viewer regardless of
 * locale instead of shifting onto the next local day (LR-485). Returns '' for an
 * unparseable input.
 */
export function formatDateTimeUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day} ${hh}:${mm} UTC`;
}

/**
 * "YYYY-MM-DD HH:MM (UTC±HH:MM)" in the viewer's LOCAL timezone — the tooltip
 * companion to formatDateTimeUtc, so a trader can map the canonical UTC snapshot
 * time onto their own clock on hover (LR-485). The explicit offset disambiguates
 * which zone "local" is. Returns '' for an unparseable input.
 */
export function formatDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  // getTimezoneOffset() is minutes that local is BEHIND UTC (positive = west), so
  // negate it to read as a conventional UTC±offset.
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const absMin = Math.abs(offsetMin);
  const offH = String(Math.floor(absMin / 60)).padStart(2, '0');
  const offM = String(absMin % 60).padStart(2, '0');
  return `${y}-${mo}-${day} ${hh}:${mm} (UTC${sign}${offH}:${offM})`;
}

/**
 * "{count} {unit}" with the unit pluralised unless count === 1, so a value of 1
 * reads "1 day" rather than "1 days" (QA finding G). Pass an explicit `plural`
 * for irregular nouns; otherwise an "s" is appended.
 */
export function pluralizeUnit(count: number, unit: string, plural?: string): string {
  return `${count} ${count === 1 ? unit : plural ?? `${unit}s`}`;
}

export function formatAmount(amount: number | string, asset: string): string {
  // Callers pass backend Decimal STRINGS (e.g. billing total_accrued) as well as
  // numbers, so coerce explicitly and guard non-numeric/absent input — a bare
  // `Math.abs('n/a')` is NaN and `NaN.toLocaleString()` renders the locale's NaN
  // word (e.g. "не число") beside the asset code (LR-208 r6 M9).
  const value = Math.abs(Number(amount));
  if (!Number.isFinite(value)) return '—';
  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'];
  if (currencies.includes(asset.toUpperCase())) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: asset }).format(value);
  }
  // Pin the locale so non-major-currency assets group consistently (en-US),
  // not by the viewer's OS locale.
  return `${value.toLocaleString('en-US')} ${asset}`;
}
